/**
 * User-facing copy for the Referral Marketing print queue.
 *
 * Its own module, not the book-order `printShopStrings`: the two screens share a
 * sidebar group but no vocabulary (jobs vs tickets, magazines vs books). Per the
 * project rule, no feature text is inlined in a component. `formatMessage`
 * interpolates `{token}` placeholders.
 */
export const rmQueueStrings = {
  sidebar: {
    // A sibling item of the Print Shop group, not a tab (ADR-001). Renamed
    // from "Magazine Queue" when each queue got its own entry
    // (abo-print-shop-menu-split).
    magazineQueue: 'Magazines',
  },
  queue: {
    pageTitle: 'Magazines',
    refresh: 'Refresh',
    searchPlaceholder: 'Search by member name or email',
    // A too-broad member search is a 400 from the server (>500 matches).
    searchTooBroad: 'That search matches too many members — narrow it down.',
    tabAll: 'All',
    empty: 'No magazine jobs in this view.',
    // The landing lane is empty while others hold jobs: say where they are.
    // `{lanes}` is each non-empty lane's `laneHas`, in lane order.
    emptyLaneHint: 'Nothing waiting to print. {lanes}.',
    laneHas: '{lane} has {count}',
    showLane: 'Show {lane} ({count})',
    error: 'Could not load the magazine queue. Try refreshing.',
    ageUnknown: '—',
    day: '{days} day',
    days: '{days} days',
    // The date cell's second line.
    since: 'since {date}',
    today: 'today',
    dayAgo: '{days} day ago',
    daysAgo: '{days} days ago',
    // The page subtitle: said once per issue, not on every row.
    windowNote:
      '{closedIssue} issue: window closed {closedOn} · {openIssue} issue: open until {openUntil}',
    // The clickable date header flips the lane between the two.
    order: {
      oldest: 'oldest first',
      newest: 'newest first',
      flip: '{column}, {order}. Click to reverse.',
    },
    columns: {
      select: 'Select {name}',
      selectPage: 'Select every job on this page',
      member: 'Member',
      issue: 'Issue',
      stage: 'Stage',
      source: 'Came from',
      format: 'Format',
      customization: 'Customization',
      inSetupFor: 'In setup for',
      waitingSinceSent: 'Waiting since sent',
      waitingToShip: 'Waiting to ship',
      shippedOn: 'Shipped on',
      cancelledOn: 'Cancelled on',
      lastMoved: 'Last moved',
    },
    selection: {
      selected: '{count} selected',
      clear: 'Clear',
      selectAll: 'Select all {count}',
      selectingAll: 'Selecting…',
      selectAllFailed: 'Could not select every job. Try again.',
      // The bar's one action, named by the lane it moves the jobs out of.
      markPrinted: 'Mark {count} as printed',
      markShipped: 'Mark {count} as shipped',
      confirmTitle: 'Mark {count} jobs as {to}?',
      confirmTitleOne: 'Mark 1 job as {to}?',
      confirmBody: '{names} move from {from} to {to}.',
      confirmMore: '{names} and {more} more move from {from} to {to}.',
      confirm: 'Mark {count} {to}',
      keep: 'Cancel',
      // DS moves the jobs one by one, so a failed request may still have moved
      // some of them: never claim that nothing changed.
      requestFailed: 'That did not finish. Reload the lane to see which jobs moved.',
      // DS takes at most this many ids per batch move.
      tooMany: 'Select at most {max} jobs per move.',
    },
    bulkResult: {
      moved: '{count} moved to {to}.',
      refused: '{count} could not be moved:',
      dismiss: 'Dismiss',
    },
  },
  stageLabels: {
    in_setup: 'In setup',
    sent_to_print: 'Sent to print',
    printed: 'Printed',
    shipped: 'Shipped',
    cancelled: 'Cancelled',
  } as Record<string, string>,
  // Who or what filed the job, as a chip.
  sourceLabels: {
    member: 'Member published',
    operator: 'Operator',
    monthly_batch: 'Monthly batch',
    hubspot_import: 'Imported from HubSpot',
    unknown: 'Not recorded',
  } as Record<string, string>,
  formatLabels: {
    physical: 'Physical',
    digital: 'Digital',
    unknown: '—',
  } as Record<string, string>,
  // Known customization values; anything new from HubSpot is shown as written.
  customizationLabels: {
    recurring_manual: 'Setup team builds it',
    default_design: 'Default design',
  } as Record<string, string>,
  personalization: {
    personalized: 'Member edited',
    carriedForward: 'Same as last month',
    // A job filed before the stamp existed: we cannot tell which it was, and
    // saying "same as last month" would manufacture a fact we do not have.
    unknown: 'Not known',
    // Tooltips, because the distinction is the whole point of the label.
    personalizedHint: 'The member edited this issue.',
    carriedForwardHint:
      'The member did not edit this issue; last month’s content was carried forward.',
    unknownHint:
      'This job was filed before the platform tracked personalisation, so it cannot be told apart.',
  },
  detail: {
    backToQueue: 'Back to Magazines',
    eyebrow: 'Magazine · {issue}',
    stepper: {
      cancelled: 'Cancelled',
    },
    hero: {
      markReady: 'Mark ready to print',
      // Where the action would be, when the job has nothing to print yet.
      markReadyBlocked: 'Upload the magazine first',
      advance: 'Mark {to}',
      terminalShipped: 'Shipped — nothing left to do',
      terminalCancelled: 'Cancelled',
      correctStage: 'Correct stage',
      cancelJob: 'Cancel job',
      cancelledLine: '{reason} — cancelled by {by}, {date}',
    },
    facts: {
      heading: 'About this job',
      source: 'Came from',
      format: 'Format',
      customization: 'Customization',
      waiting: 'Waiting',
      inSetupFor: 'In setup for',
      memberSite: 'Member site',
      proof: 'Proof',
      shippedMagazine: 'Shipped magazine',
      open: 'Open',
      // A stated condition, never an empty cell (the requirement).
      memberSiteAbsent:
        'Not available yet — the monthly site rebuild had not finished when this job was filed.',
      absent: 'Not available',
      hubspotService: 'HubSpot service {id}',
      hubspotTicket: 'HubSpot ticket {id}',
      importRun: 'Import run {id}',
    },
    files: {
      heading: 'Print files',
      memberNote: 'Member’s note:',
      badge: 'PDF',
      preview: 'Preview',
      download: 'Download',
      magazine: 'Magazine',
      covers: 'Covers',
      generated: 'Made automatically',
      uploadedBy: '{size} · uploaded by {by}, {date}',
      upload: 'Upload magazine PDF',
      replace: 'Replace',
      uploading: 'Uploading… {percent}%',
      uploaded: 'Magazine uploaded.',
      uploadFailed: 'The magazine could not be uploaded.',
      notPdf: 'Choose a PDF file.',
      tooLarge: 'The file is larger than 100 MB.',
      // Distinct wording per reason — a file that will exist vs one that will not.
      notRenderedYet: 'Not rendered yet',
      notGenerated: 'Never generated',
      lookupFailed: 'Could not check — try again',
      unavailable: 'Unavailable',
      noMagazine: 'No magazine PDF yet',
    },
    history: {
      heading: 'History',
      empty: 'No history yet.',
      correctionBadge: 'Correction',
      actorSystem: 'System',
      actorMember: 'Member',
      created: 'Created in {to}',
      imported: 'Imported from HubSpot into {to}',
      moved: 'Moved from {from} to {to}',
      corrected: 'Corrected from {from} to {to}',
      cancelled: 'Cancelled',
      magazineUploaded: 'Uploaded the magazine',
      magazineReplaced: 'Replaced the magazine',
      filingUpgraded: 'Filed for print, moved from {from} to {to}',
      filingMerged: 'Filed again; missing details were added',
      importMerged: 'HubSpot import added missing details',
      importReverted: 'HubSpot import was undone',
      fileLine: '{name}, {size}',
      reason: 'Reason: {reason}',
    },
    cancel: {
      title: 'Cancel this job?',
      reasonLabel: 'Why is it cancelled?',
      reasonPlaceholder: 'E.g. member cancelled RM',
      reasonTooShort: 'Write at least {min} characters.',
      reasonTooLong: 'Keep it under {max} characters.',
      keep: 'Keep job',
      confirm: 'Cancel job',
    },
    correct: {
      title: 'Correct this job’s stage?',
      hint: 'Only to fix a stage set by mistake. This is recorded as a correction, not as progress.',
      stageLabel: 'Move it back to',
      reasonLabel: 'Why is this being corrected?',
      reasonPlaceholder: 'Briefly, so the history explains itself',
      reasonRequired: 'A reason is required for a correction.',
      confirm: 'Correct the stage',
      keep: 'Cancel',
    },
    advance: {
      title: 'Move this job forward?',
      body: 'This will move the job from {from} to {to}.',
      confirm: 'Yes, mark {to}',
      keep: 'Cancel',
    },
    toast: {
      advanced: 'Moved to {to}.',
      corrected: 'Stage corrected to {to}.',
      ready: 'Sent to print.',
      cancelled: 'Job cancelled.',
      actionFailed: 'That did not work. The job was not changed.',
      downloadFailed: 'Could not open that file.',
      previewBlocked:
        'The browser blocked the preview tab. Allow pop-ups for this site and try again.',
    },
  },
  // ABO's own text per DS refusal code. Read `code`, never `error`.
  refusals: {
    RM_PRINT_JOB_NOT_FOUND: 'This print job no longer exists.',
    RM_PRINT_JOB_STAGE_CONFLICT:
      'This job moved in the meantime. It was reloaded; check it and try again.',
    RM_PRINT_JOB_NO_PRINT_FILE:
      'Upload the magazine PDF before marking this job ready to print.',
    RM_PRINT_JOB_NOT_CANCELLABLE: 'A shipped or cancelled job cannot be cancelled.',
    RM_PRINT_JOB_FILE_LOCKED:
      'The magazine can no longer change: this job is printed, shipped or cancelled.',
    RM_PRINT_JOB_ACTION_FAILED: 'Something went wrong with this job. Try again.',
    RM_PRINT_FILE_NO_BUCKET: 'Magazine uploads are not set up in this environment.',
    RM_PRINT_MAGAZINE_SUPERSEDED:
      'A newer version of this magazine exists. Send that one to print instead.',
    RM_PRINT_MAGAZINE_PREVIEW: 'A preview magazine cannot be sent to print.',
    RM_PRINT_MAGAZINE_NOT_DONE:
      'This magazine is not ready yet: it is still being generated, failed, or was already sent.',
    RM_PRINT_MAGAZINE_NO_FILES: 'This magazine has no PDF to print.',
    PRINT_FILE_MISSING: 'Choose a file to upload.',
    PRINT_FILE_TOO_LARGE: 'The file is larger than 100 MB.',
    PRINT_FILE_NOT_PDF: 'That file is not a PDF.',
  } as Record<string, string>,
  sendToPrint: {
    failed: 'Could not send this magazine to print.',
  },
};

/** `{token}` interpolation. Same shape as the book-order screen's helper. */
export const formatMessage = (
  template: string,
  vars: Record<string, string | number> = {},
): string =>
  template.replace(/\{(\w+)\}/g, (match, key: string) =>
    Object.prototype.hasOwnProperty.call(vars, key)
      ? String(vars[key])
      : match,
  );
